export const CHANGE_ID = "SC-2027-014";

export const FIELD_VALUES = [
    "HUMAN_AUTHORISED",
    "RULES_ENGINE",
    "AI_RECOMMENDATION_HUMAN_APPROVAL",
    "AUTOMATED_WITHIN_MANDATE",
    "DELEGATED_AUTHORITY",
];

export const PACKAGE_V1 = {
    id: CHANGE_ID,
    version: "1",
    currentStandard: "Payment usage profile v3.4 (synthetic)",
    proposedStandard: "Payment usage profile v3.5 (synthetic)",
    field: "ExecutionDecisionMethod",
    definition:
        "A structured element recording the source of execution authority for a defined payment workflow.",
    permittedValues: FIELD_VALUES,
    affectedRoles: [
        "Originating Institution",
        "Intermediary / Scheme Operator",
        "Receiving Institution",
        "Settlement / Reporting Role",
    ],
    affectedMessages: ["Customer credit transfer initiation", "Interbank credit transfer", "Payment status report"],
    effectiveDate: "2027-11-15",
    effectiveDateLabel: "Illustrative demonstration date",
    transitionWindow: "180-day dual-acceptance window",
    testCases: 6,
    stewardSignature: "steward-sig:9f2c…a41d (synthetic)",
    packageHash: "sha256:74b1…c9e2 (synthetic immutable reference)",
    reason: [
        "A person making a decision",
        "A rules engine applying predetermined policy",
        "An AI making a recommendation",
        "An automated action occurring within a prior mandate",
        "A third party acting under delegated authority",
    ],
};

export const PACKAGE_V2_REVISIONS = [
    "Clarify the difference between rules-driven execution and automated action under mandate",
    "Add a transition code for legacy channels",
    "Provide two additional test cases",
    "Separate message activation from reporting activation",
    "Extend the reporting-role transition period",
    "Define treatment of outstanding records",
];

export const NODES = [
    {
        id: "steward",
        name: "Standards Steward",
        short: "Steward",
        desc: "The authoritative standards or scheme publisher.",
        steward: true,
        controls: [
            "Canonical definition",
            "Change identifier",
            "Approved values",
            "Semantic meaning",
            "Dependencies",
            "Effective date",
            "Transition conditions",
            "Test cases",
        ],
        may: ["Propose", "Clarify", "Approve", "Publish", "Supersede", "Withdraw a change"],
        mayNot: [
            "Inspect participants' private systems",
            "Approve an individual payment",
            "Override a participant's legal or operational authority",
        ],
    },
    {
        id: "orig",
        name: "Originating Institution",
        short: "Originator",
        desc: "The organisation initiating the instruction.",
        controls: [
            "Customer mandate",
            "Channel information",
            "Execution-decision evidence",
            "Source-system mapping",
            "Internal approval policy",
        ],
    },
    {
        id: "inter",
        name: "Intermediary / Scheme Operator",
        short: "Intermediary",
        desc: "A network or intermediary role.",
        controls: [
            "Routing rules",
            "Scheme validation",
            "Usage-guideline conformance",
            "Network transition requirements",
            "Exception routing",
        ],
    },
    {
        id: "recv",
        name: "Receiving Institution",
        short: "Receiver",
        desc: "The beneficiary-side organisation.",
        controls: [
            "Acceptance policy",
            "Beneficiary information",
            "Screening and risk controls",
            "Downstream field availability",
            "Reconciliation logic",
        ],
    },
    {
        id: "settle",
        name: "Settlement / Reporting Role",
        short: "Settlement",
        desc: "A settlement institution, reporting destination or other final accountable role.",
        controls: [
            "Settlement eligibility",
            "Reporting requirements",
            "Final-state confirmation",
            "Retention and reconciliation rules",
        ],
    },
];

export const PARTICIPANTS = NODES.filter((n) => !n.steward);

export const IMPACTS = {
    orig: {
        result: "AMBER — MAPPING CHANGE REQUIRED",
        tone: "amber",
        findings: [
            "Field available for AI-assisted channel",
            "Field unavailable for two legacy channels",
            "Customer mandate service already captures authority type",
            "Source-system mapping required",
            "Test coverage incomplete",
        ],
        meta: {
            policyVersion: "ORIG-POL v12.3",
            impactedComponents: 7,
            dataAvailability: "Partial — two legacy channels cannot supply the field",
            testStatus: "Coverage incomplete",
            readinessDate: "2027-10-01",
            unresolvedQuestions: 1,
        },
    },
    inter: {
        result: "GREEN — VALIDATION READY",
        tone: "green",
        findings: [
            "New code list accepted",
            "Schema validation available",
            "Backward-compatible transition possible",
            "Exception code required for missing field",
        ],
        meta: {
            policyVersion: "NET-VAL v8.1",
            impactedComponents: 3,
            dataAvailability: "Full",
            testStatus: "Reference tests passed",
            readinessDate: "2027-07-15",
            unresolvedQuestions: 0,
        },
    },
    recv: {
        result: "AMBER — INTERPRETATION REQUIRED",
        tone: "amber",
        findings: [
            "Distinction between RULES_ENGINE and AUTOMATED_WITHIN_MANDATE unclear",
            "Reconciliation logic affected",
            "Receiving system can store but does not currently act on the value",
        ],
        meta: {
            policyVersion: "RCV-ACC v5.9",
            impactedComponents: 4,
            dataAvailability: "Storable — not yet actioned",
            testStatus: "Pending clarification",
            readinessDate: "2027-10-20",
            unresolvedQuestions: 2,
        },
    },
    settle: {
        result: "RED — EFFECTIVE DATE NOT ACHIEVABLE",
        tone: "red",
        findings: [
            "Reporting schema dependency",
            "External vendor release required",
            "Outstanding records cannot be reconstructed",
            "Minimum implementation lead time exceeds proposed window",
        ],
        meta: {
            policyVersion: "SET-REP v9.0",
            impactedComponents: 9,
            dataAvailability: "Dependent on external vendor release",
            testStatus: "Blocked",
            readinessDate: "2028-03-01",
            unresolvedQuestions: 0,
        },
    },
};

export const IMPACT_SUMMARY = [
    { label: "GREEN", count: 1, tone: "green" },
    { label: "AMBER", count: 2, tone: "amber" },
    { label: "RED", count: 1, tone: "red" },
    { label: "CLARIFICATIONS", count: 3, tone: "slate" },
    { label: "DEPENDENCIES", count: 2, tone: "slate" },
];

export const READINESS_STATES = ["PACKAGE INSTALLED", "TESTING", "READY", "ACTIVATION SCHEDULED"];

export const RUNTIME_CHAIN = [
    "INSTRUCTION RECEIVED",
    "LOCAL AUTHORITY CONFIRMED",
    "NETWORK RULES VALIDATED",
    "ACCEPTED",
    "SETTLED",
];

export const RUNTIME_CHECKS = {
    orig: {
        title: "Sets",
        rows: ["ExecutionDecisionMethod: AI_RECOMMENDATION_HUMAN_APPROVAL"],
        evidence: [
            "AI recommendation receipt",
            "Authorised human approval",
            "Customer mandate",
            "Active local policy version",
            "Applicable standard package",
        ],
    },
    inter: {
        title: "Checks",
        rows: [
            "Field present",
            "Permitted code",
            "Applicable usage profile",
            "Correct version",
            "Transition conditions satisfied",
        ],
    },
    recv: {
        title: "Checks",
        rows: [
            "Message can be accepted",
            "Value is compatible with local control policy",
            "Required evidence reference is present",
        ],
    },
    settle: {
        title: "Checks",
        rows: [
            "Final instruction is eligible",
            "Correct version has been used",
            "Reporting treatment is valid",
        ],
    },
};

export const EXCEPTION_CHAIN = ["EXCEPTION ASSIGNED", "CORRECTED", "RECHECKED", "RELEASED"];

export const EXCEPTION_RECEIPT_FIELDS = [
    ["Sending node", "Originating Institution (legacy channel LC-02)"],
    ["Message version", "Payment usage profile v3.5 (synthetic)"],
    ["Local policy version", "NET-VAL v8.1"],
    ["Failed field", "ExecutionDecisionMethod"],
    ["Rejected value", "AI_AUTOMATED"],
    ["Applicable standard package", `${CHANGE_ID} v2`],
    ["Required corrective action", "Resubmit with a value from the approved code list"],
];

export const FEEDBACK_STATS = [
    "2.8% of instructions required manual clarification",
    "73% of those exceptions originated in legacy channels",
    "RULES_ENGINE was confused with AUTOMATED_WITHIN_MANDATE",
    "No settlement failures occurred after correction",
];

export const STEWARD_DOES_NOT_RECEIVE = [
    "Customer identities",
    "Transaction amounts",
    "Account data",
    "Private internal rules",
    "Raw system logs",
];

export const STEWARD_RECEIVES = [
    "Exception category",
    "Affected field",
    "Applicable version",
    "Role type",
    "Frequency",
    "Resolution outcome",
    "Proposed clarification",
];

export const SCENARIOS = [
    { key: "success", label: "Successful coordinated activation" },
    { key: "legacy", label: "Legacy system not ready" },
    { key: "interpretation", label: "Interpretation conflict" },
    { key: "invalid", label: "Invalid runtime value" },
];

export const VIEWS = [
    { key: "network", label: "Network view" },
    { key: "steward", label: "Standards steward view" },
    { key: "orig", label: "Originating institution view" },
    { key: "recv", label: "Receiving institution view" },
];

export const DOWNSTREAM = [
    "AUTHORITATIVE CHANGE",
    "LOCAL IMPACT ASSESSMENT",
    "SIMULATION AND READINESS",
    "CONTROLLED ACTIVATION",
    "GOVERNED EXECUTION",
];

export const UPSTREAM = [
    "RUNTIME OUTCOMES",
    "EXCEPTION RECEIPTS",
    "IMPLEMENTATION FRICTION",
    "READINESS AND IMPACT EVIDENCE",
    "STEWARD CLARIFICATION",
];

export const CLOSING_CHAIN = ["PUBLISH", "ASSESS", "CLARIFY", "TEST", "ACTIVATE", "EXECUTE", "LEARN"];

export const DISCLAIMERS = [
    "This is a synthetic demonstration.",
    "No live financial transactions or customer information are used.",
    "The scenario does not represent a current ISO 20022 change.",
    "KATLAS does not determine ISO policy or regulatory interpretation.",
    "Standards, schemes and regulated institutions retain their existing authority.",
    "KATLAS governs the exchange of change, readiness, decision and outcome evidence across participant boundaries.",
];

export const KEY_LINE =
    "Source authority is stewarded. Execution authority is distributed. Compatibility is proved through receipts.";

const base = () => ({
    demonstration: "SIMULATED",
    synthetic: true,
    generated_at: "2027-06-01T00:00:00Z (illustrative)",
});

export const RECEIPTS = [
    {
        key: "publication",
        name: "Publication receipt",
        proves: [
            "Who published",
            "What changed",
            "Applicable version",
            "Publication authority",
            "Effective-date proposal",
        ],
        json: {
            ...base(),
            receipt_type: "publication",
            published_by: "Standards Steward",
            publication_authority: "Authoritative standards / scheme publisher (synthetic)",
            change_package: CHANGE_ID,
            package_version: "1",
            new_field: "ExecutionDecisionMethod",
            permitted_values: FIELD_VALUES,
            proposed_effective_date: "2027-11-15 (Illustrative demonstration date)",
            package_hash: "sha256:74b1…c9e2 (synthetic immutable reference)",
            steward_signature: "steward-sig:9f2c…a41d (synthetic)",
        },
    },
    {
        key: "impact",
        name: "Impact receipt",
        proves: [
            "Which role assessed",
            "Local policy version",
            "Readiness state",
            "Dependencies",
            "Clarification requests",
        ],
        json: {
            ...base(),
            receipt_type: "impact",
            assessing_role: "Settlement / Reporting Role",
            change_package: `${CHANGE_ID} v1`,
            local_policy_version: "SET-REP v9.0",
            assessment_result: "RED — EFFECTIVE DATE NOT ACHIEVABLE",
            dependencies: ["Reporting schema dependency", "External vendor release required"],
            clarification_requests: ["Treatment of outstanding records"],
            proposed_readiness_date: "2028-03-01",
            role_signature: "role-sig:c31a…77be (synthetic)",
        },
    },
    {
        key: "readiness",
        name: "Readiness receipt",
        proves: ["Package installed", "Reference tests run", "Results", "Planned activation state"],
        json: {
            ...base(),
            receipt_type: "readiness",
            role: "Originating Institution",
            change_package: `${CHANGE_ID} v2`,
            package_installed: true,
            reference_tests_run: 8,
            test_results: "8 passed, 0 failed",
            planned_activation_state: "ACTIVATION SCHEDULED",
            local_policy_version: "ORIG-POL v12.4",
            role_signature: "role-sig:5be0…19dd (synthetic)",
        },
    },
    {
        key: "runtime",
        name: "Runtime decision receipt",
        proves: ["Proposed action", "Applicable versions", "Local authority result", "Network outcome"],
        json: {
            ...base(),
            receipt_type: "runtime_decision",
            instruction_ref: "SYN-PAY-2027-3141 (synthetic)",
            proposed_action: "Customer credit transfer with ExecutionDecisionMethod",
            execution_decision_method: "AI_RECOMMENDATION_HUMAN_APPROVAL",
            standards_package: `${CHANGE_ID} v2`,
            usage_profile: "Payment usage profile v3.5 (synthetic)",
            role_policy_versions: {
                originating: "ORIG-POL v12.4",
                intermediary: "NET-VAL v8.1",
                receiving: "RCV-ACC v6.0",
                settlement: "SET-REP v9.1",
            },
            role_decisions: {
                originating: "LOCAL AUTHORITY CONFIRMED",
                intermediary: "NETWORK RULES VALIDATED",
                receiving: "ACCEPTED",
                settlement: "SETTLED",
            },
            evidence_references: [
                "ai-recommendation-receipt:ref-8842 (synthetic)",
                "human-approval:ref-8843 (synthetic)",
                "customer-mandate:ref-1120 (synthetic)",
            ],
            exceptions: [],
            timestamps: {
                received: "2027-11-16T09:02:11Z",
                settled: "2027-11-16T09:02:14Z",
            },
            final_outcome: "SETTLED",
        },
    },
    {
        key: "feedback",
        name: "Feedback receipt",
        proves: [
            "Recurring implementation or runtime issue",
            "Affected standard element",
            "Bounded impact",
            "Resolution or proposed change",
        ],
        json: {
            ...base(),
            receipt_type: "feedback",
            reporting_role: "Receiving Institution",
            affected_standard_element: "ExecutionDecisionMethod",
            applicable_version: `${CHANGE_ID} v2`,
            exception_category: "Code interpretation",
            issue: "RULES_ENGINE confused with AUTOMATED_WITHIN_MANDATE",
            frequency: "Recurring — 2.8% of instructions (synthetic demonstration data)",
            resolution_outcome: "Corrected on resubmission; no settlement failures",
            proposed_clarification: "Add decision-tree guidance to the code-value definitions",
            role_signature: "role-sig:aa04…f210 (synthetic)",
        },
    },
];

export const downloadJson = (obj, filename) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};
